(() => {
  if (window.mdcSingleVideoInitialized) return;
  window.mdcSingleVideoInitialized = true;

  const externalSelector = 'details.mdc-install-guide__external';
  const componentSelector = 'deferred-media, mdc-product-video, mdc-story-video';
  const ambientSelector = '[data-mdc-ambient-gallery]';
  const ambientModalSelector = 'dialog[data-mdc-ambient-modal]';
  let active = null;
  let intent = null;

  const visible = (video) => {
    if (!video.isConnected || document.hidden) return false;
    if (document.pictureInPictureElement === video) return true;
    if (video.closest('[hidden], dialog:not([open]), details:not([open]), slideshow-slide[aria-hidden="true"]')) return false;
    const rect = video.getBoundingClientRect();
    return rect.width > 0 && rect.height > 0 && rect.bottom > 0 && rect.top < window.innerHeight &&
      rect.right > 0 && rect.left < window.innerWidth && getComputedStyle(video).visibility === 'visible';
  };

  const ambientGroup = (video) => {
    if (!video.muted || video.closest(ambientModalSelector)) return null;
    return video.closest(ambientSelector);
  };

  const isAmbientGroup = (element) => element instanceof Element && element.matches(ambientSelector);
  const playingInGroup = (group) => Array.from(group.querySelectorAll('video')).filter((video) =>
    ambientGroup(video) === group && !video.paused && !video.ended && visible(video));

  const occupied = () => {
    if (active instanceof HTMLVideoElement) return !active.paused && !active.ended && visible(active);
    if (isAmbientGroup(active)) return active.isConnected && playingInGroup(active).length > 0;
    return active?.isConnected && active.open;
  };

  const syncAmbientGroups = () => {
    const busy = occupied();
    document.querySelectorAll(ambientSelector).forEach((group) => {
      const suspended = Boolean(busy && active !== group);
      if (group.hasAttribute('data-mdc-ambient-suspended') === suspended) return;
      group.toggleAttribute('data-mdc-ambient-suspended', suspended);
      group.dispatchEvent(new CustomEvent('mdc:ambient:state', { detail: { suspended } }));
    });
  };

  const pauseVideo = (video) => {
    const group = ambientGroup(video);
    if (typeof group?.pauseAmbientVideo === 'function') group.pauseAmbientVideo(video);
    const component = video.closest(componentSelector);
    const method = component?.matches('deferred-media') ? 'pauseMedia' :
      component?.matches('mdc-product-video') ? 'pausePlayback' : 'pause';
    try {
      if (typeof component?.[method] === 'function') component[method]();
    } catch (_) {}
    if (!video.paused) video.pause();
  };

  const pauseOthers = (except, keepGroup = null) => {
    document.querySelectorAll('video').forEach((video) => {
      if (video !== except && !video.paused && (!keepGroup || ambientGroup(video) !== keepGroup)) pauseVideo(video);
    });
  };

  const closeExternal = (except) => {
    document.querySelectorAll(externalSelector).forEach((details) => {
      if (details !== except && details.open) {
        details.open = false;
        details.querySelector('.mdc-install-guide__player')?.replaceChildren();
      }
    });
  };

  const observer = 'IntersectionObserver' in window ? new IntersectionObserver((entries) => {
    entries.forEach(({ target }) => {
      if ((target === active || (isAmbientGroup(active) && ambientGroup(target) === active)) && !visible(target)) {
        observer.unobserve(target);
        pauseVideo(target);
        if (target === active || (isAmbientGroup(active) && playingInGroup(active).length === 0)) active = null;
        syncAmbientGroups();
      }
    });
  }) : null;

  const recordIntent = (event) => {
    if (!event.isTrusted || !(event.target instanceof Element)) return;
    if (event.type === 'keydown' && !['Enter', ' ', 'MediaPlayPause'].includes(event.key)) return;
    const ambientControl = event.target.closest('[data-mdc-ambient-toggle]');
    if (ambientControl?.closest(ambientSelector)) {
      const scope = ambientControl.closest('[data-mdc-ambient-card]');
      const video = scope?.querySelector('video');
      if (video && video.paused) intent = { scope, expires: performance.now() + 5000 };
      return;
    }
    const momentControl = event.target.closest('mdc-video-moments [data-moment-control], mdc-video-moments .mdc-video-moments__frame');
    if (momentControl) {
      const scope = momentControl.closest('[data-moment-card]');
      const video = scope?.querySelector('video');
      if (video && video.paused) intent = { scope, expires: performance.now() + 5000 };
      return;
    }
    const control = event.target.closest('video, deferred-media button, .mdc-product-video__toggle, .mdc-product-video__scenes button, .mdc-story-video__toggle, mdc-story-video .mdc-reviewed-video__viewport, mdc-story-video.mdc-story-video');
    if (!control) return;
    const scope = control.closest(componentSelector) || control;
    const video = scope.matches('video') ? scope : scope.querySelector('video');
    if (!video || video.paused) intent = { scope, expires: performance.now() + 5000 };
  };

  const onPlay = (event) => {
    const video = event.target;
    if (!(video instanceof HTMLVideoElement) || video.paused || video.ended) return;
    const modal = video.closest(ambientModalSelector);
    const manual = Boolean(modal?.open || (intent && performance.now() <= intent.expires && intent.scope.contains(video)));
    if (manual) intent = null;
    if (!visible(video)) {
      pauseVideo(video);
      return;
    }

    const group = ambientGroup(video);
    if (group) {
      if (active !== group && occupied() && !manual) {
        pauseVideo(video);
        syncAmbientGroups();
        return;
      }
      const maximum = Math.min(4, Math.max(1, Number.parseInt(group.dataset.maxPlaying, 10) || 4));
      const others = playingInGroup(group).filter((item) => item !== video);
      if (others.length >= maximum) {
        if (!manual) {
          pauseVideo(video);
          return;
        }
        others.slice(0, others.length - maximum + 1).forEach(pauseVideo);
      }
      if (active !== group) {
        active = group;
        observer?.disconnect();
        closeExternal();
        pauseOthers(video, group);
      }
      observer?.observe(video);
      syncAmbientGroups();
      return;
    }

    if (active !== video && occupied() && !manual) {
      pauseVideo(video);
      return;
    }
    active = video;
    observer?.disconnect();
    observer?.observe(video);
    syncAmbientGroups();
    closeExternal();
    pauseOthers(video);
  };

  const onPauseOrEnd = (event) => {
    if (!document.querySelector(ambientSelector)) return;
    const video = event.target;
    if (!(video instanceof HTMLVideoElement)) return;
    if (!video.paused && !video.ended) return;
    if (active === video) {
      const modal = video.closest(ambientModalSelector);
      active = modal?.open ? modal : null;
      observer?.unobserve(video);
    } else if (isAmbientGroup(active) && ambientGroup(video) === active) {
      observer?.unobserve(video);
      if (playingInGroup(active).length === 0) active = null;
    }
    syncAmbientGroups();
  };

  const onToggle = (event) => {
    const details = event.target;
    if (!(details instanceof Element) || !details.matches(externalSelector)) return;
    if (details.open) {
      active = details;
      intent = null;
      observer?.disconnect();
      syncAmbientGroups();
      pauseOthers();
      closeExternal(details);
    } else if (active === details) {
      active = null;
      syncAmbientGroups();
    }
  };

  const onAmbientModal = (event) => {
    const modal = event.detail?.dialog;
    if (!(modal instanceof Element) || !modal.matches(ambientModalSelector) || !modal.closest(ambientSelector)) return;
    if (event.detail.open && modal.open) {
      active = modal;
      intent = null;
      observer?.disconnect();
      syncAmbientGroups();
      pauseOthers();
      closeExternal();
    } else if (active === modal || (active instanceof HTMLVideoElement && modal.contains(active))) {
      active = null;
      syncAmbientGroups();
    }
  };

  ['pointerdown', 'click', 'keydown'].forEach((type) => document.addEventListener(type, recordIntent, true));
  document.addEventListener('play', onPlay, true);
  document.addEventListener('pause', onPauseOrEnd, true);
  document.addEventListener('ended', onPauseOrEnd, true);
  document.addEventListener('toggle', onToggle, true);
  document.addEventListener('mdc:ambient-modal', onAmbientModal);
  document.addEventListener('mdc:ambient-ready', syncAmbientGroups);
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) {
      active = null;
      intent = null;
      observer?.disconnect();
      pauseOthers();
      closeExternal();
    }
    syncAmbientGroups();
  });
  document.querySelectorAll(`${externalSelector}[open]`).forEach((target) => onToggle({ target }));
  document.querySelectorAll('video').forEach((target) => onPlay({ target }));
  syncAmbientGroups();
})();
